/**
 * GraphQL queries and hooks — businessUnit pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type {
  BusinessUnit,
  BusinessUnitConnection,
  BusinessUnitFilter,
} from '../types/businessUnit';

export const BUSINESS_UNIT_FIELDS = gql`
  fragment BusinessUnitFields on BusinessUnit {
    entityId
    entityType
    name
    description
    unitType
    lifecycleStatus
    parentBusinessUnitId
    code
    tags
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_BUSINESS_UNIT = gql`
  ${BUSINESS_UNIT_FIELDS}
  query GetBusinessUnit($id: ID!) {
    businessUnit(id: $id) {
      ...BusinessUnitFields
    }
  }
`;

export const LIST_BUSINESS_UNITS = gql`
  ${BUSINESS_UNIT_FIELDS}
  query ListBusinessUnits($filter: BusinessUnitFilter, $first: Int, $after: String) {
    businessUnits(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...BusinessUnitFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useBusinessUnit(id: string): QueryResult<{ businessUnit: BusinessUnit | null }> {
  return useQuery(GET_BUSINESS_UNIT, {
    variables: { id },
    skip: !id,
  });
}

export function useBusinessUnits(
  filter?: BusinessUnitFilter,
  first: number = 250
): QueryResult<{ businessUnits: BusinessUnitConnection }> {
  return useAllPages<{ businessUnits: BusinessUnitConnection }>(
    LIST_BUSINESS_UNITS,
    (data) => data?.businessUnits,
    { filter },
    first
  );
}
